import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { useState, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../configureStore";
import { useTheme } from "../../ThemeContext";
import { PromptDetail } from "../../interface/prompts";
import PromptList from "../organisms/PromptList";
import PromptEditor from "../organisms/PromptEditor";
import PromptTester from "../organisms/PromptTester";

const PromptManagement = () => {
    const navigate = useNavigate();
    const { mode, toggleColorMode } = useTheme();
    const selectedPrompt = useSelector<RootState, PromptDetail | null>(({ prompts }) => prompts.selectedPrompt);

    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
    const [sourceContent, setSourceContent] = useState<string>("");
    const [currentContent, setCurrentContent] = useState<string>("");

    // Draggable divider state
    const [editorHeight, setEditorHeight] = useState<number>(60); // percentage
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleCreateNew = () => {
        setIsCreating(true);
        setIsDuplicating(false);
        setSourceContent("");
        setCurrentContent("");
    };

    const handleDuplicate = () => {
        if (selectedPrompt) {
            setSourceContent(selectedPrompt.content);
            setIsCreating(true);
            setIsDuplicating(true);
        }
    };

    const handleCancelCreate = () => {
        setIsCreating(false);
        setIsDuplicating(false);
        setSourceContent("");
    };

    const handleContentChange = (content: string) => {
        setCurrentContent(content);
    };

    // Draggable divider handlers
    const handleMouseDown = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !containerRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const relativeY = e.clientY - containerRect.top;
        const percentage = (relativeY / containerRect.height) * 100;

        // Clamp between 20% and 80%
        const clampedPercentage = Math.min(Math.max(percentage, 20), 80);
        setEditorHeight(clampedPercentage);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add/remove global mouse listeners for dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'row-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <Container maxWidth="xl" sx={{ py: 2, minHeight: '100vh', position: 'relative' }}>
            {/* Header Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/returns')} color="primary" size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        Prompt Management
                    </Typography>
                </Box>
                <IconButton onClick={toggleColorMode} color="inherit" size="small">
                    {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
            </Box>

            {/* Main Content - Split View */}
            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 80px)', minHeight: 600 }}>
                {/* Left Panel - Prompt List */}
                <Paper sx={{ width: 320, flexShrink: 0, overflow: 'hidden' }}>
                    <PromptList
                        onCreateNew={handleCreateNew}
                        onDuplicate={handleDuplicate}
                        selectedPromptName={selectedPrompt?.name || null}
                    />
                </Paper>

                {/* Right Panel - Editor and Tester */}
                <Paper
                    ref={containerRef}
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    {/* Editor Section */}
                    <Box sx={{
                        height: (selectedPrompt && !isCreating) ? `${editorHeight}%` : '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                    >
                        <PromptEditor
                            isCreating={isCreating}
                            isDuplicating={isDuplicating}
                            sourceContent={sourceContent}
                            onCancelCreate={handleCancelCreate}
                            onContentChange={handleContentChange}
                        />
                    </Box>

                    {/* Tester Section */}
                    {(selectedPrompt || isCreating) && !isCreating && (
                        <>
                            {/* Draggable Divider */}
                            <Box
                                onMouseDown={handleMouseDown}
                                sx={{
                                    height: 8,
                                    cursor: 'row-resize',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: isDragging ? 'action.selected' : 'divider',
                                    borderTop: 1,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    transition: isDragging ? 'none' : 'background-color 0.2s',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <DragHandleIcon
                                    sx={{
                                        fontSize: 16,
                                        color: 'text.secondary',
                                    }}
                                />
                            </Box>
                            <Box sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                p: 2,
                            }}
                            >
                                <Typography variant="h6" gutterBottom>
                                    Test Prompt
                                </Typography>
                                <PromptTester currentContent={currentContent} />
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default PromptManagement;
